import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSettings, useUpdateSettings } from "../hooks/useSettings";
import type { GuardrailsMode, PaperSize } from "../api/types";
import { LANGUAGES } from "../lib/languages";

const PAPER_SIZES: PaperSize[] = ["A5", "A4", "Letter"];

const GUARDRAILS_MODES: { value: GuardrailsMode; label: string; detail: string }[] = [
  {
    value: "block",
    label: "Block",
    detail: "Recommended - stop builds when a transcript looks like a prompt-injection attack.",
  },
  {
    value: "flag",
    label: "Flag only",
    detail: "Annotate the findings but always build the book.",
  },
  {
    value: "off",
    label: "Off",
    detail: "Skip screening entirely.",
  },
];

export default function Settings() {
  const { data, isLoading, isError } = useSettings();
  const update = useUpdateSettings();
  const initialized = useRef(false);

  const [paperSize, setPaperSize] = useState<PaperSize>("A5");
  const [language, setLanguage] = useState("en");
  const [preciseMode, setPreciseMode] = useState(false);
  const [maxVideoHours, setMaxVideoHours] = useState(2.5);
  const [maxTranscriptChars, setMaxTranscriptChars] = useState(400_000);
  const [guardrailsMode, setGuardrailsMode] = useState<GuardrailsMode>("block");

  useEffect(() => {
    if (data && !initialized.current) {
      initialized.current = true;
      setPaperSize(data.paper_size);
      setLanguage(data.language);
      setPreciseMode(data.precise_mode);
      setMaxVideoHours(data.max_video_hours);
      setMaxTranscriptChars(data.max_transcript_chars);
      setGuardrailsMode(data.guardrails_mode);
    }
  }, [data]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    update.mutate({
      paper_size: paperSize,
      language,
      precise_mode: preciseMode,
      max_video_hours: maxVideoHours,
      max_transcript_chars: maxTranscriptChars,
      guardrails_mode: guardrailsMode,
    });
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <p className="text-muted">Loading settings…</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <p className="text-danger">Could not load settings. Is the backend running?</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <div className="flex items-start justify-between gap-4">
        <h1 className="font-serif text-3xl font-semibold text-text">Settings</h1>
        <div className="flex items-center gap-3">
          {update.isSuccess ? <span className="text-sm text-accent-strong">Saved.</span> : null}
          {update.isError ? <span className="text-sm text-danger">{update.error.message}</span> : null}
          <button
            type="submit"
            form="settings-form"
            disabled={update.isPending}
            className="rounded-md bg-accent px-5 py-2.5 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {update.isPending ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
      <p className="mt-2 text-muted">
        Defaults for every paper you create. You can still override them per paper in the create dialog.
      </p>

      <form id="settings-form" onSubmit={handleSubmit} className="mt-8 space-y-8">
        <div className="max-w-xs">
          <label htmlFor="paper-size" className="mb-1 block text-sm font-medium text-text">
            Default paper size
          </label>
          <select
            id="paper-size"
            value={paperSize}
            onChange={(e) => setPaperSize(e.target.value as PaperSize)}
            className="w-full rounded-md border border-border bg-surface pl-3 pr-9 py-2 text-sm text-text"
          >
            {PAPER_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div>
          <h2 className="mb-3 text-sm font-semibold text-text">Book</h2>
          <div className="space-y-4">
            <div className="max-w-xs">
              <label htmlFor="language" className="mb-1 block text-sm font-medium text-text">
                Book language
              </label>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full rounded-md border border-border bg-surface pl-3 pr-9 py-2 text-sm text-text"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted">
                When the video is in a different language, the book is translated faithfully - nothing is
                summarized away in translation.
              </p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  checked={preciseMode}
                  onChange={(e) => setPreciseMode(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-accent"
                />
                Precise mode
              </label>
              <p className="mt-1 text-xs text-muted">
                Writes with more care and allows a second repair round, for noticeably better prose on
                demanding material. Papers take longer. Either way every chapter is checked against its
                source - accuracy is never what gets traded.
              </p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="mb-3 text-sm font-semibold text-text">Limits</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="max-video-hours" className="mb-1 block text-sm font-medium text-text">
                Maximum video length (hours)
              </label>
              <input
                id="max-video-hours"
                type="number"
                // step="any": with min=0.05, a fixed step puts the browser's
                // valid-value grid at 0.05, 0.55, … — the default 2.5 itself
                // fails native validation and silently blocks the form.
                step="any"
                min={0.05}
                max={24}
                value={maxVideoHours}
                onChange={(e) => setMaxVideoHours(e.target.value === "" ? 0 : Number(e.target.value))}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              />
              <p className="mt-1 text-xs text-muted">
                Videos longer than this are rejected up front, before Boort spends time transcribing them.
              </p>
            </div>
            <div>
              <label htmlFor="max-transcript-chars" className="mb-1 block text-sm font-medium text-text">
                Maximum transcript size (characters)
              </label>
              <input
                id="max-transcript-chars"
                type="number"
                // step="any" for the same reason: min=1000 + step=10000 makes
                // the default 400000 a stepMismatch.
                step="any"
                min={1000}
                max={10000000}
                value={maxTranscriptChars}
                onChange={(e) => setMaxTranscriptChars(e.target.value === "" ? 0 : Number(e.target.value))}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              />
              <p className="mt-1 text-xs text-muted">
                Catches short videos with implausibly huge caption files, a common sign of a corrupted or hostile
                transcript.
              </p>
            </div>
          </div>
        </div>

        <fieldset>
          <legend className="mb-3 text-sm font-semibold text-text">Guardrails</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {GUARDRAILS_MODES.map((mode) => (
              <label
                key={mode.value}
                className={
                  "flex cursor-pointer flex-col gap-1 rounded-lg border p-4 text-sm transition-colors " +
                  (guardrailsMode === mode.value
                    ? "border-accent bg-accent-soft"
                    : "border-border bg-surface hover:border-muted")
                }
              >
                <span className="flex items-center gap-2 font-medium text-text">
                  <input
                    type="radio"
                    name="guardrails-mode"
                    value={mode.value}
                    checked={guardrailsMode === mode.value}
                    onChange={() => setGuardrailsMode(mode.value)}
                    className="h-4 w-4 text-accent"
                  />
                  {mode.label}
                </span>
                <p className="text-xs text-muted">{mode.detail}</p>
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted">
            Transcript hardening (spotlighting) stays on in every mode, regardless of this setting.
          </p>
        </fieldset>

      </form>
    </div>
  );
}
